const blockedPatterns = [
  /\bignore\s+(all|any)\s+previous\s+instructions\b/i,
  /\bexfiltrate\b/i,
  /\bsteal\s+(password|credentials|secret)/i,
  /\bmalware\b/i,
  /\bransomware\b/i
];

export function runContentSafetyCheck(text) {
  const value = String(text || '');
  const blocked = blockedPatterns.some((pattern) => pattern.test(value));

  return {
    allowed: !blocked,
    reason: blocked ? 'Content matched a blocked safety policy.' : null
  };
}

export function requireSafeContent(text, code) {
  const result = runContentSafetyCheck(text);

  if (!result.allowed) {
    const error = new Error(result.reason);
    if (code) error.code = code;
    throw error;
  }

  return text;
}

// ── Scope guardrail (T3 — cross-screen-overview-and-live-share.md §2.8) ─────
//
// runContentSafetyCheck is a SAFETY filter (harmful output). This is a SCOPE
// boundary: does the question fit what the bot is DECLARED to handle? It runs
// deterministically (keyword overlap between the bot's declared domain and the
// user's message) — no model call, so a clean decline costs ZERO AO. A scope
// decline is a graceful redirect ("I cover sales figures; that legal question
// is outside what I'm set up for"), never a best-effort wandering answer —
// this matters most when a third party is watching a live share.
//
// The check is deliberately PERMISSIVE: it only declines on a substantive
// request (3+ meaningful tokens) with ZERO overlap against a declared domain.
// Short utterances, greetings, meta-questions ("what can you do?") and bots
// with no declared scope always pass. Missing an edge decline costs a wander;
// over-declining costs a frustrated owner — permissive is the right bias.

export const SCOPE_DECLINE_CODE = 'OUT_OF_SCOPE';

const STOPWORDS = new Set([
  'the','and','for','are','with','you','your','what','how','why','when',
  'where','who','which','this','that','these','those','can','could','would',
  'should','will','shall','have','has','had','been','being','was','were','does',
  'do','did','is','am','not','about','from','them','they','their','there',
  'here','please','tell','make','get','give','want','need','some','any','all',
  'just','like','know','think','into','than','then','also','very','really'
]);

// Meta / small-talk that must never be scope-declined.
const META_PATTERNS = [
  /^(hi|hello|hey|yo|good\s*(morning|afternoon|evening))\b/,
  /\b(thank|thanks|great|awesome|perfect)\b/,
  /\b(what can you do|what do you do|who are you|help me understand|cancel|stop|reset)\b/,
  /\b(ignore that|never mind|as you were)\b/
];

// Words too generic to count as a scope signal on either side.
const WEAK_TERMS = new Set([
  'business','question','questions','help','answer','work','works','working',
  'plan','plans','planning','time','thing','things','people','someone','better',
  'recommend','recommendation','suggest','advice','option','options','thinking'
]);

function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9+\-\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

function scopeTermSet(bot) {
  const scope = bot?.knowledge_scope || bot?.knowledgeScope; // client alias (pbWorkspace camelCase)
  const capabilities = Array.isArray(bot?.capabilities) ? bot.capabilities : [];

  // Only bots with a DECLARED domain are judged. Name/role alone (e.g. a
  // brand-new custom bot the owner hasn't scoped yet) never derive a domain —
  // the check must not over-decline against a role title.
  if (!scope && !capabilities.length) return new Set();

  const sources = [
    scope,
    bot?.role,
    bot?.name,
    bot?.category,
    bot?.moduleSlug,
    bot?.module,
    ...capabilities
  ];

  const terms = new Set();

  for (const source of sources) {
    for (const raw of tokenize(source)) {
      const word = raw.replace(/^[+-]+|[+-]+$/g, '');
      if (word.length >= 3 && !STOPWORDS.has(word) && !WEAK_TERMS.has(word)) {
        terms.add(word);
      }
      // kebab/snake capabilities split into their parts too
      // (financial-strategy → financial + strategy).
    }
  }

  // Also split compound tokens so 'financial-strategy' contributes
  // 'financial' and 'strategy', and scope sentences contribute real words.
  const compound = [...terms].flatMap((term) => {
    const parts = term.split(/[+-]/).filter((part) => part.length >= 3);
    return parts.length > 1 ? parts : [];
  });

  for (const part of compound) {
    if (!STOPWORDS.has(part) && !WEAK_TERMS.has(part)) terms.add(part);
  }

  return terms;
}

/**
 * Deterministic scope check. bot carries knowledge_scope (text), capabilities
 * (array of strings), role/category/module. Returns { inScope, reason?,
 * matched? } — never throws, never calls a model.
 */
export function runScopeCheck(bot, userMessage) {
  const raw = String(userMessage || '');
  const messageWords = tokenize(raw);
  const meaningful = messageWords.filter((word) => (
    word.length >= 4 && !STOPWORDS.has(word) && !WEAK_TERMS.has(word)
  ));

  // Nothing declared to judge against → allow (never block the unknown).
  const terms = scopeTermSet(bot);
  if (!terms.size) return { inScope: true };

  // Small talk / meta questions always pass.
  const lowered = raw.toLowerCase();
  if (META_PATTERNS.some((pattern) => pattern.test(lowered))) {
    return { inScope: true };
  }

  // Too short to be a substantive request → allow.
  const rawWords = raw.trim().split(/\s+/).filter(Boolean);
  if (rawWords.length < 3) return { inScope: true };

  const matched = meaningful.filter((word) => terms.has(word));

  if (matched.length > 0) {
    return { inScope: true, matched };
  }

  // Zero domain overlap. Decline only when the message reads as a real ask
  // (imperative/question opener or enough content words) — otherwise allow.
  const opener = messageWords[0];
  const askShape = (
    rawWords.length >= 6
    || /^(can|could|please|how|why|what|when|where|is|are|do|does|fix|build|write|make|create|debug|check|review|draft|help|explain|set)\b/.test(opener)
  );

  if (meaningful.length >= 3 && askShape) {
    return {
      inScope: false,
      reason: 'The request shares no terms with this bot’s declared scope.'
    };
  }

  return { inScope: true };
}

/**
 * The clean professional decline a scope-checked bot answers with
 * (cross-screen-overview-and-live-share.md §2.8). Names the bot's domain so
 * the decline doubles as a redirect to the right team.
 */
export function scopeDeclineMessage(bot) {
  const name = bot?.name || 'this assistant';

  let domain = null;
  if (Array.isArray(bot?.capabilities) && bot.capabilities.length) {
    domain = bot.capabilities
      .slice(0, 3)
      .map((item) => String(item).replace(/[-_]/g, ' '))
      .join(', ');
  }
  if (!domain && bot?.role) {
    domain = String(bot.role).toLowerCase().replace(/ lead$/, ' strategy');
  }
  const scope = bot?.knowledgeScope || bot?.knowledge_scope;
  if (!domain && scope) {
    // e.g. "Questions about this business financial figures: …" → the tail
    // after the colon is the real domain description.
    const scopeText = String(scope);
    const tail = scopeText.includes(':') ? scopeText.slice(scopeText.indexOf(':') + 1).trim() : scopeText;
    domain = tail.replace(/\.$/, '');
  }
  if (!domain) domain = 'a focused domain';

  return `${name} is set up for ${domain} — that question falls outside what I cover, so I won't improvise an answer you can't rely on. Ask the team that owns that area, or keep me on ${domain}.`;
}
